// frontend/src/pages/BlogArticle.js
//
// Single blog article (/blog/:slug). Renders the structured article from
// blogData.js via ArticleBody, plus per-article SEO/GEO: BlogPosting + FAQPage +
// BreadcrumbList JSON-LD. Hebrew-only content, always rendered RTL.

import React from "react";
import { useParams, Navigate, Link as RouterLink } from "react-router-dom";
import { Box, Typography, Link as MuiLink, Chip } from "@mui/material";
import { ArrowLeft } from "lucide-react";
import AuthLayout from "../components/AuthLayout";
import Seo, { SITE_URL, DEFAULT_OG_IMAGE } from "../components/Seo";
import ArticleBody, { renderInline } from "../components/blog/ArticleBody";
import BlogImage from "../components/blog/BlogImage";
import ShareButtons from "../components/blog/ShareButtons";
import {
  articles,
  getArticle,
  getRelatedArticles,
  formatHebrewDate,
  BLOG_AUTHOR,
  BLOG_BASE,
} from "../content/blog/blogData";
// Absolute-URL helpers come from blogIndex (blogData's own source), matching how
// Blog.js imports them. blogIndex is already in this module's graph - no cost.
import { articleUrl, blogIndexUrl } from "../content/blog/blogIndex";

const HEAD_FONT = '"Heebo", sans-serif';
const BODY_FONT = '"DM Sans", sans-serif';
// Two teals, split by WCAG threshold rather than by taste:
//   ACCENT      - large text (h2) + decoration (borders, hovers). Needs 3:1.
//                 The old #00A896 was 2.98:1 and missed by 0.02; this is 3.27:1
//                 and is visually indistinguishable from it.
//   ACCENT_TEXT - small text (breadcrumb, chips, links, meta). Needs 4.5:1.
//                 5.47:1 on white, 5.12:1 on the #EDFAF8 chip background.
const ACCENT = "#00A08D";
const ACCENT_TEXT = "#0F766E";
// Muted grey for byline/breadcrumb/meta. The old #94A3B8 was 2.56:1; this is 4.76:1.
const MUTED = "#64748B";

// Compressed JPEG built by scripts/optimize-images.js for social/OG previews
// (WhatsApp/Facebook drop the ~1.2MB source PNG). 1200x630 to match og:image:*.
const OG_IMAGE = { width: 1200, height: 630, type: "image/jpeg" };
const ogImageUrl = (image) =>
  image ? `${SITE_URL}${image.replace(/\.png$/i, "-og.jpg")}` : DEFAULT_OG_IMAGE;

// Strip inline markup for structured-data (plain-text) fields.
const toPlainText = (s) =>
  s
    .replace(/\[([^\]]+)\]\(([^)]+)\)/g, "$1")
    .replace(/\*\*/g, "")
    .replace(/\s*\n\s*/g, " ")
    .trim();

// Exported for __tests__/blogCanonicalUrls.test.js, which sweeps the REAL return
// value for slash-less blog URLs. Testing the helpers alone is tautological - a
// new hand-built URL added here would sail past it.
export function buildArticleJsonLd(article) {
  // articleUrl()/blogIndexUrl() delegate to Seo's buildCanonicalUrl, so the
  // structured data matches the canonical tag + sitemap exactly (trailing slash
  // = the only URL GCS serves 200 on; the slash-less form 301s).
  const url = articleUrl(article.slug);
  const publisher = {
    "@type": "Organization",
    name: "Leadify AI",
    logo: {
      "@type": "ImageObject",
      url: `${SITE_URL}/android-chrome-512x512.png`,
    },
  };

  const blogPosting = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline: article.metaTitle || article.title,
    description: article.metaDescription,
    datePublished: article.datePublished,
    dateModified: article.dateModified || article.datePublished,
    author: { "@type": "Organization", name: "Leadify AI", url: SITE_URL },
    publisher,
    mainEntityOfPage: { "@type": "WebPage", "@id": url },
    image: ogImageUrl(article.image),
    inLanguage: "he",
    keywords: (article.tags || []).join(", "),
  };

  const breadcrumb = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "דף הבית", item: `${SITE_URL}/` },
      { "@type": "ListItem", position: 2, name: "בלוג", item: blogIndexUrl() },
      {
        "@type": "ListItem",
        position: 3,
        name: article.title,
        item: url,
      },
    ],
  };

  const blocks = [blogPosting, breadcrumb];

  if (article.faq && article.faq.length) {
    blocks.push({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: article.faq.map((item) => ({
        "@type": "Question",
        name: item.q,
        acceptedAnswer: { "@type": "Answer", text: toPlainText(item.a) },
      })),
    });
  }

  return blocks;
}

const ArticleContent = ({ article }) => {
  const published = formatHebrewDate(article.datePublished);
  const modified = formatHebrewDate(article.dateModified);
  const showModified = article.dateModified && article.dateModified !== article.datePublished;

  // Next article (wraps around) — keeps readers in the blog + adds internal links.
  const idx = articles.findIndex((a) => a.slug === article.slug);
  const nextArticle = idx >= 0 ? articles[(idx + 1) % articles.length] : null;
  const related = getRelatedArticles(article.slug, 3);

  // User-facing: every WhatsApp/Facebook share used to resolve via a 301.
  const shareUrl = articleUrl(article.slug);

  return (
    <Box
      dir="rtl"
      sx={{ width: "100%", background: "#FFFFFF", py: { xs: 4, md: 6 } }}
    >
      <Seo
        title={article.metaTitle || article.title}
        description={article.metaDescription}
        path={`${BLOG_BASE}/${article.slug}`}
        lang="he"
        type="article"
        image={ogImageUrl(article.image)}
        imageWidth={OG_IMAGE.width}
        imageHeight={OG_IMAGE.height}
        imageType={OG_IMAGE.type}
        jsonLd={buildArticleJsonLd(article)}
      />

      <Box sx={{ maxWidth: 760, mx: "auto", px: { xs: 2.5, md: 3 } }}>
        {/* Breadcrumb */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.75,
            mb: { xs: 2, md: 3 },
            fontFamily: BODY_FONT,
            fontSize: "13.5px",
            color: MUTED,
            flexWrap: "wrap",
          }}
        >
          <MuiLink component={RouterLink} to={BLOG_BASE} sx={{ color: ACCENT_TEXT, textDecoration: "none", fontWeight: 600, "&:hover": { textDecoration: "underline" } }}>
            בלוג
          </MuiLink>
          <Box component="span" sx={{ opacity: 0.6 }}>‹</Box>
          <Box component="span" sx={{ color: MUTED }}>{article.title}</Box>
        </Box>

        {/* Title */}
        <Typography
          component="h1"
          sx={{
            fontFamily: HEAD_FONT,
            fontWeight: 800,
            fontSize: { xs: "28px", md: "38px" },
            lineHeight: 1.3,
            color: "#1F2D3D",
            letterSpacing: "-0.01em",
            mb: { xs: 1.5, md: 2 },
          }}
        >
          {article.title}
        </Typography>

        {/* Byline */}
        <Typography
          sx={{
            fontFamily: BODY_FONT,
            fontStyle: "italic",
            fontSize: "13.5px",
            color: MUTED,
            mb: { xs: 2, md: 2.5 },
          }}
        >
          {`פורסם: ${published}`}
          {showModified ? ` · עודכן: ${modified}` : ""}
          {` · מאת ${BLOG_AUTHOR}`}
        </Typography>

        {/* Tags + inline share pill (same row) */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 0.75,
            mb: { xs: 2.5, md: 3 },
          }}
        >
          {article.tags?.map((tag) => (
            <Chip
              key={tag}
              label={tag}
              size="small"
              sx={{
                background: "#EDFAF8",
                color: ACCENT_TEXT,
                fontFamily: BODY_FONT,
                fontWeight: 600,
                fontSize: "12px",
                height: 24,
              }}
            />
          ))}
          <ShareButtons
            variant="inline"
            url={shareUrl}
            title={article.title}
            text={article.excerpt}
          />
        </Box>

        {/* Hero image (gradient fallback if missing) */}
        {article.image && (
          <Box
            sx={{
              width: "100%",
              aspectRatio: "1.91 / 1",
              borderRadius: "16px",
              overflow: "hidden",
              mb: { xs: 2.5, md: 3 },
              background: "linear-gradient(135deg, #EDFAF8, #FDF3F7)",
            }}
          >
            <BlogImage
              src={article.image}
              alt={article.title}
              loading="eager"
              sizes="(max-width: 800px) 92vw, 760px"
            />
          </Box>
        )}

        {/* TL;DR */}
        <Box
          sx={{
            borderInlineStart: `4px solid ${ACCENT}`,
            background: "linear-gradient(110deg, #EDFAF8, #F8FDFF)",
            borderRadius: "0 12px 12px 0",
            px: { xs: 2, md: 2.5 },
            py: { xs: 1.5, md: 1.75 },
            mb: { xs: 2, md: 3 },
          }}
        >
          <Typography
            sx={{
              fontFamily: BODY_FONT,
              fontSize: { xs: "15px", md: "16.5px" },
              lineHeight: 1.8,
              color: "#3D4A5C",
              "& strong": { fontWeight: 700, color: "#1F2D3D" },
              // WCAG AA: link colour + a non-colour cue. See the ArticleBody
              // wrapper below for the rationale.
              "& a": {
                color: ACCENT_TEXT,
                textDecorationLine: "underline",
                textUnderlineOffset: "3px",
                textDecorationThickness: "1px",
              },
            }}
          >
            <strong>בקצרה: </strong>
            {renderInline(article.tldr)}
          </Typography>
        </Box>

        {/* Body.
            Inline links come from components/shared/inlineMarkup, whose LINK_SX is
            ALSO used by the pricing plan cards - so the fix is scoped here rather
            than in that shared module, which would recolour /payment too.
            Two WCAG failures are closed at once:
              1.4.3 contrast - #00A896 was 2.98:1 on white, below the 4.5:1 bar for
                    body-size text; ACCENT_TEXT is 5.47:1.
              1.4.1 use of colour - links inside a paragraph were distinguished from
                    surrounding text by hue alone (underline appeared on hover only),
                    so the underline must be persistent, not a hover affordance. */}
        <Box
          sx={{
            "& a": {
              color: ACCENT_TEXT,
              textDecorationLine: "underline",
              // Hebrew descenders (ק ך ן ף ץ) crowd a default-offset underline;
              // 3px drops the rule clear of them. Pinned thickness so the line
              // does not thicken under bold runs.
              textUnderlineOffset: "3px",
              textDecorationThickness: "1px",
            },
          }}
        >
          <ArticleBody blocks={article.blocks} />
        </Box>

        {/* FAQ */}
        {article.faq && article.faq.length > 0 && (
          <Box sx={{ mt: { xs: 4, md: 6 }, pt: { xs: 3, md: 4 }, borderTop: "1px solid #E2E8F0" }}>
            <Typography
              component="h2"
              sx={{
                fontFamily: HEAD_FONT,
                fontWeight: 800,
                fontSize: { xs: "22px", md: "26px" },
                color: ACCENT,
                mb: { xs: 2, md: 2.5 },
              }}
            >
              שאלות נפוצות
            </Typography>
            {article.faq.map((item, i) => (
              <Box key={i} sx={{ mb: { xs: 2, md: 2.5 } }}>
                <Typography
                  component="h3"
                  sx={{
                    fontFamily: HEAD_FONT,
                    fontWeight: 700,
                    fontSize: { xs: "16px", md: "18px" },
                    color: "#1F2D3D",
                    mb: 0.5,
                  }}
                >
                  {item.q}
                </Typography>
                <Typography
                  sx={{
                    fontFamily: BODY_FONT,
                    fontSize: { xs: "15px", md: "16px" },
                    lineHeight: 1.8,
                    color: "#4B5563",
                    "& strong": { fontWeight: 700, color: "#374151" },
                  }}
                >
                  {renderInline(item.a)}
                </Typography>
              </Box>
            ))}
          </Box>
        )}

        {/* CTA */}
        {article.cta && (
          <Box
            sx={{
              mt: { xs: 4, md: 5 },
              px: { xs: 2.5, md: 3 },
              py: { xs: 2.25, md: 2.5 },
              borderRadius: "16px",
              // White CTA text is 17px/400, so it needs 4.5:1 across the WHOLE
              // gradient, not just the dark end. #0D9488 gave only 3.74:1 at the
              // light end; #0F8177 is 4.74:1 and reads as the same teal.
              background: "linear-gradient(135deg, #0F8177 0%, #0F766E 100%)",
              boxShadow: "0 8px 28px rgba(15,118,110,0.28)",
            }}
          >
            <Typography
              sx={{
                fontFamily: BODY_FONT,
                fontSize: { xs: "15.5px", md: "17px" },
                lineHeight: 1.8,
                color: "#FFFFFF",
                "& strong": { fontWeight: 700 },
              }}
            >
              {renderInline(article.cta, {
                color: "#FFFFFF",
                fontWeight: 700,
                textDecoration: "underline",
                "&:hover": { textDecoration: "underline", opacity: 0.9 },
              })}
            </Typography>
          </Box>
        )}

        {/* Share row (bottom) — caught after finishing, near the CTA */}
        <Box sx={{ mt: { xs: 3, md: 3.5 } }}>
          <ShareButtons
            url={shareUrl}
            title={article.title}
            text={article.excerpt}
            label="אהבתם? שתפו:"
          />
        </Box>

        {/* Related articles (shared-tag cluster) */}
        {related.length > 0 && (
          <Box sx={{ mt: { xs: 4, md: 6 }, pt: { xs: 3, md: 4 }, borderTop: "1px solid #E2E8F0" }}>
            <Typography
              component="h2"
              sx={{
                fontFamily: HEAD_FONT,
                fontWeight: 800,
                fontSize: { xs: "20px", md: "24px" },
                color: ACCENT,
                mb: { xs: 2, md: 2.5 },
              }}
            >
              מאמרים קשורים
            </Typography>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr 1fr" },
                gap: { xs: 2, md: 2.5 },
              }}
            >
              {related.map((r) => (
                <MuiLink
                  key={r.slug}
                  component={RouterLink}
                  to={`${BLOG_BASE}/${r.slug}`}
                  sx={{
                    textDecoration: "none",
                    display: "flex",
                    flexDirection: "column",
                    border: "1px solid #E2E8F0",
                    borderRadius: "14px",
                    overflow: "hidden",
                    background: "#FFFFFF",
                    transition: "border-color 0.2s ease, box-shadow 0.2s ease",
                    "&:hover": { borderColor: ACCENT, boxShadow: "0 6px 18px rgba(0,168,150,0.12)" },
                  }}
                >
                  <Box
                    sx={{
                      width: "100%",
                      aspectRatio: "1.91 / 1",
                      overflow: "hidden",
                      background: "linear-gradient(135deg, #EDFAF8, #FDF3F7)",
                    }}
                  >
                    {r.image && (
                      <BlogImage
                        src={r.image}
                        alt={r.title}
                        sizes="(max-width: 600px) 92vw, 260px"
                      />
                    )}
                  </Box>
                  <Typography
                    sx={{
                      p: 1.5,
                      fontFamily: HEAD_FONT,
                      fontWeight: 700,
                      fontSize: "15px",
                      color: "#2D3748",
                      lineHeight: 1.4,
                    }}
                  >
                    {r.title}
                  </Typography>
                </MuiLink>
              ))}
            </Box>
          </Box>
        )}

        {/* Bottom nav: back to list + next article (keeps readers in the blog) */}
        <Box
          sx={{
            mt: { xs: 4, md: 5 },
            pt: { xs: 2.5, md: 3 },
            borderTop: "1px solid #E2E8F0",
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            gap: 2,
            justifyContent: "space-between",
            alignItems: { xs: "flex-start", sm: "center" },
          }}
        >
          <MuiLink
            component={RouterLink}
            to={BLOG_BASE}
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 0.5,
              color: ACCENT_TEXT,
              fontFamily: BODY_FONT,
              fontWeight: 600,
              fontSize: "15px",
              textDecoration: "none",
              flexShrink: 0,
              "&:hover": { textDecoration: "underline" },
            }}
          >
            {/* RTL: arrow points right (back) visually via flip */}
            <ArrowLeft size={18} style={{ transform: "scaleX(-1)" }} />
            חזרה לכל המאמרים
          </MuiLink>

          {nextArticle && (
            <MuiLink
              component={RouterLink}
              to={`${BLOG_BASE}/${nextArticle.slug}`}
              sx={{
                textDecoration: "none",
                maxWidth: { sm: "62%" },
                textAlign: { xs: "start", sm: "end" },
                "&:hover .next-title": { textDecoration: "underline" },
              }}
            >
              <Typography
                sx={{
                  fontFamily: BODY_FONT,
                  fontSize: "12.5px",
                  color: MUTED,
                  mb: 0.25,
                }}
              >
                המאמר הבא
              </Typography>
              <Box
                className="next-title"
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 0.5,
                  color: ACCENT_TEXT,
                  fontFamily: HEAD_FONT,
                  fontWeight: 700,
                  fontSize: { xs: "14.5px", md: "16px" },
                  lineHeight: 1.4,
                }}
              >
                {nextArticle.title}
                <ArrowLeft size={17} style={{ flexShrink: 0 }} />
              </Box>
            </MuiLink>
          )}
        </Box>
      </Box>
    </Box>
  );
};

const BlogArticle = () => {
  const { slug } = useParams();
  const article = getArticle(slug);

  if (!article) {
    return <Navigate to={BLOG_BASE} replace />;
  }

  return (
    <AuthLayout isPublic={true}>
      <ArticleContent article={article} />
    </AuthLayout>
  );
};

export default BlogArticle;
